// Favorites store for the public salon cards.
//
// Backed by React Query so every surface that renders a SalonCard
// (HomePage, SearchPage, map info-window, ProfilePage tab) shares one
// cached Set of salonIds. The toggle mutation is optimistic — the heart
// fills/unfills the moment the user clicks — and rolls back on error.
//
// Unauthenticated callers get an empty Set + a `requireAuth` flag so UI
// components can gate the click on login redirect without branching on
// `user?.id` at every callsite.
import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import useAuth from './useAuth';

const QK_FAVORITES = ['favorites', 'me'];

// Quick O(1) presence check — the query caches an array of ids, we
// convert to a Set on every consumer read. Cheap for typical list
// sizes (a heavy user might have ~50 favorites).
function toSet(list) {
  return new Set(Array.isArray(list) ? list : []);
}

export default function useFavorites() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Fetch once on mount, cache aggressively — the list rarely changes
  // outside of the user's own toggles, which we apply optimistically
  // anyway.
  const query = useQuery({
    queryKey: QK_FAVORITES,
    enabled: Boolean(user?.id),
    queryFn: async () => {
      const { data } = await api.get('/users/me/favorites');
      return Array.isArray(data?.salonIds) ? data.salonIds : [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const favoriteSet = toSet(query.data);

  const mutation = useMutation({
    mutationFn: async (salonId) => {
      const { data } = await api.post(`/users/toggle-favorite/${salonId}`);
      return { salonId, favorite: Boolean(data?.favorite) };
    },
    // Optimistic update — patch the cached list BEFORE the network
    // request resolves. React Query's onMutate/onError/onSettled lets
    // us snapshot the prior state, apply the optimistic patch, and
    // roll back cleanly on failure.
    onMutate: async (salonId) => {
      await queryClient.cancelQueries({ queryKey: QK_FAVORITES });
      const prev = queryClient.getQueryData(QK_FAVORITES) || [];
      const wasFavorite = prev.includes(salonId);
      const next = wasFavorite
        ? prev.filter((id) => id !== salonId)
        : [...prev, salonId];
      queryClient.setQueryData(QK_FAVORITES, next);
      return { prev };
    },
    onError: (err, _salonId, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(QK_FAVORITES, ctx.prev);
      toast.error(err?.response?.data?.message || 'Nu am putut salva favorita.');
    },
    // Invalidate the per-salon queries that might render a "saved"
    // badge (the ProfilePage favorites tab pulls hydrated rows via
    // its own key).
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
    },
  });

  // Single public toggle — gates on auth + runs the mutation. Returns
  // true if the toggle was initiated, false if redirected to login;
  // callers typically ignore the return value.
  const toggleFavorite = useCallback(
    (salonId) => {
      if (!user?.id) {
        // Preserve the page the user was on so LoginPage can bounce
        // them back after signing in (mirrors the existing 401
        // interceptor's pattern).
        try {
          sessionStorage.setItem(
            'postAuthRedirect',
            window.location.pathname + window.location.search,
          );
        } catch { /* noop */ }
        toast('Autentifica-te ca sa salvezi saloane favorite.', { icon: '🔒' });
        navigate('/login');
        return false;
      }
      if (!salonId) return false;
      mutation.mutate(salonId);
      return true;
    },
    [mutation, navigate, user?.id],
  );

  const isFavorite = useCallback(
    (salonId) => favoriteSet.has(salonId),
    [favoriteSet],
  );

  return {
    favoriteIds: query.data || [],
    isFavorite,
    toggleFavorite,
    isLoading: query.isLoading,
    isAuthenticated: Boolean(user?.id),
  };
}
